import { useLayoutEffect, useEffect, useMemo, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"

import useAppStore from "../../../store/useAppStore"
import useLayoutStore from "../../../store/useLayoutStore"
import LoadingSpinner from "../../elements/LoadingSpinner"
import AdminEmployeeMonthLogsModal from "./AdminEmployeeMonthLogsModal"
import AdminStatsMonthReportModal from "./AdminStatsMonthReportModal"

const MAX_SCALE_HOURS = 200
const LOG_ROW_FLASH_MS = 520

const AdminStatsView = () => {
    const isDarkTheme = useLayoutStore((state) => state.isDarkTheme)
    const isTouchDevice = useLayoutStore((state) => state.isTouchDevice)
    const statsDateFilter = useAppStore((state) => state.statsDateFilter)
    const ensureStatsMonthData = useAppStore((state) => state.ensureStatsMonthData)
    const loadingKarnetData = useAppStore((state) => state.loadingKarnetData)
    const loadingCalendarData = useAppStore((state) => state.loadingCalendarData)
    const karnetData = useAppStore((state) => state.karnetData)
    const openEmployeeMonthLogsModal = useAppStore((state) => state.openEmployeeMonthLogsModal)
    const showStatsMonthReport = useAppStore((state) => state.showStatsMonthReport)
    const statsRowFlashUserId = useAppStore((state) => state.statsRowFlashUserId)
    const statsRowFlashTick = useAppStore((state) => state.statsRowFlashTick)
    const [hoveredLegend, setHoveredLegend] = useState(null)
    const [flashingRowId, setFlashingRowId] = useState(null)

    const monthKey = `${statsDateFilter.year}-${String(statsDateFilter.month + 1).padStart(2, "0")}`

    useEffect(() => {
        setFlashingRowId(null)
    }, [monthKey])

    useEffect(() => {
        if (!statsRowFlashUserId) return undefined
        setFlashingRowId(String(statsRowFlashUserId))
        const timer = window.setTimeout(() => setFlashingRowId(null), LOG_ROW_FLASH_MS)
        return () => window.clearTimeout(timer)
    }, [statsRowFlashUserId, statsRowFlashTick])

    useLayoutEffect(() => {
        ensureStatsMonthData(statsDateFilter.month, statsDateFilter.year)
    }, [statsDateFilter.month, statsDateFilter.year, ensureStatsMonthData])

    const hourSteps = useMemo(() => (
        Array.from({ length: 20 }, (_, idx) => (idx + 1) * 10)
    ), [])

    const workHoursMarkers = useMemo(() => {
        const year = statsDateFilter.year
        const month = statsDateFilter.month
        const daysInMonth = new Date(year, month + 1, 0).getDate()

        let workingDays = 0
        for (let day = 1; day <= daysInMonth; day += 1) {
            const dayOfWeek = new Date(year, month, day).getDay()
            if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                workingDays += 1
            }
        }

        const hoursAt8 = workingDays * 8
        const hoursAt7_5 = workingDays * 7.5

        return {
            workingDays,
            hoursAt8,
            hoursAt7_5,
            line8Percent: Math.min((hoursAt8 / MAX_SCALE_HOURS) * 100, 100),
            line7_5Percent: Math.min((hoursAt7_5 / MAX_SCALE_HOURS) * 100, 100)
        }
    }, [statsDateFilter.month, statsDateFilter.year])

    const rows = useMemo(() => {
        if (!karnetData) return []

        return Object.entries(karnetData).map(([userId, user]) => {
            const totalMinutes = Object.values(user?.days || {}).reduce((sum, value) => {
                return sum + (Number(value) || 0)
            }, 0)
            const totalHours = totalMinutes / 60
            const hoursPart = Math.floor(totalMinutes / 60)
            const minutesPart = totalMinutes % 60
            const exactTime = `${String(hoursPart).padStart(2, "0")}:${String(minutesPart).padStart(2, "0")}`

            return {
                userId,
                firstName: user?.ime || "",
                lastName: user?.prezime || "",
                totalHours,
                exactTime
            }
        })
    }, [karnetData])

    return (
        <>
            <AnimatePresence mode="wait">
                {(loadingKarnetData || loadingCalendarData) ? (
                    <LoadingSpinner />
                ) : (
                    <>
                        <motion.div
                            key={monthKey}
                            className="kv-table-container asv-table-container"
                            initial={{ opacity: 0, x: 40 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, }}
                            transition={{ duration: 0.19, ease: "easeOut" }}
                        >
                            <table className={`kv-table asv-table ${isDarkTheme ? "dark-table" : ""}`}>
                                <thead>
                                    <tr>
                                        <th>Ime</th>
                                        <th>Prezime</th>
                                        {hourSteps.map((step) => (
                                            <th key={step}>{step}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row, idx) => (
                                        <AdminStatsRow
                                            key={row.userId}
                                            row={row}
                                            idx={idx}
                                            hourSteps={hourSteps}
                                            isDarkTheme={isDarkTheme}
                                            workHoursMarkers={workHoursMarkers}
                                            isFlashing={String(row.userId) === String(flashingRowId)}
                                            onRowClick={() => openEmployeeMonthLogsModal(row.userId)}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </motion.div>
                        <div className="asv-footer">
                            <button
                                type="button"
                                className={`cv-new-absence-button asv-month-report-button ${isDarkTheme ? "dark-big-btn" : ""}`}
                                onClick={() => showStatsMonthReport()}
                            >
                                Prikaz mesecnog izvestaja
                            </button>
                            <div className={`asv-legend ${isDarkTheme ? "asv-legend-dark" : ""}`}>
                                <span
                                    className="asv-legend-item"
                                    onMouseEnter={() => !isTouchDevice && setHoveredLegend("effective")}
                                    onMouseLeave={() => !isTouchDevice && setHoveredLegend(null)}
                                >
                                    <span className="asv-legend-dot asv-legend-dot-7-5" />
                                    Efektivan rad
                                    <AnimatePresence>
                                        {hoveredLegend === "effective" && (
                                            <motion.div
                                                key="legend-effective"
                                                className={`v-header-button-tooltip asv-legend-tooltip ${isDarkTheme ? "tooltip-dark" : "tooltip-white"}`}
                                                initial={{ opacity: 0, y: -10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -10 }}
                                                transition={{ duration: 0.18, ease: "easeOut" }}
                                            >
                                                {`${workHoursMarkers.workingDays} radnih dana × 7.5h = ${workHoursMarkers.hoursAt7_5}h`}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </span>
                                <span
                                    className="asv-legend-item"
                                    onMouseEnter={() => !isTouchDevice && setHoveredLegend("presence")}
                                    onMouseLeave={() => !isTouchDevice && setHoveredLegend(null)}
                                >
                                    <span className="asv-legend-dot asv-legend-dot-8" />
                                    Ukupno prisustvo
                                    <AnimatePresence>
                                        {hoveredLegend === "presence" && (
                                            <motion.div
                                                key="legend-presence"
                                                className={`v-header-button-tooltip asv-legend-tooltip ${isDarkTheme ? "tooltip-dark" : "tooltip-white"}`}
                                                initial={{ opacity: 0, y: -10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -10 }}
                                                transition={{ duration: 0.18, ease: "easeOut" }}
                                            >
                                                {`${workHoursMarkers.workingDays} radnih dana × 8h = ${workHoursMarkers.hoursAt8}h`}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </span>
                            </div>
                        </div>
                    </>
                )}
            </AnimatePresence>
            <AdminEmployeeMonthLogsModal />
            <AdminStatsMonthReportModal />
        </>
    )
}

const AdminStatsRow = ({ row, idx, hourSteps, isDarkTheme, workHoursMarkers, isFlashing, onRowClick }) => {
    const widthPercent = Math.max(0, Math.min((row.totalHours / MAX_SCALE_HOURS) * 100, 100))

    return (
        <tr
            className={`asv-row asv-emp-day-log-row${isFlashing ? " asv-emp-day-log-row--flash" : ""}`}
            onClick={onRowClick}
        >
            <td className={`asv-name-cell ${isDarkTheme ? "dark-color" : ""}`}>{row.firstName}</td>
            <td className={`asv-name-cell ${isDarkTheme ? "dark-color" : ""}`}>{row.lastName}</td>
            <td className="asv-bar-cell" colSpan={hourSteps.length}>
                <div className="asv-bar-track">
                    <div className="asv-target-line asv-target-line-7-5" style={{ left: `${workHoursMarkers.line7_5Percent}%` }} />
                    <div className="asv-target-line asv-target-line-8" style={{ left: `${workHoursMarkers.line8Percent}%` }} />
                    <motion.div
                        className="asv-bar-fill"
                        style={{ width: `${widthPercent}%` }}
                        initial={{ scaleX: 0, originX: 0 }}
                        animate={{ scaleX: 1, originX: 0 }}
                        transition={{ duration: 0.28, ease: "easeOut", delay: idx * 0.015 }}
                    />
                    <span className={`asv-bar-time-label asv-bar-time-label-hover ${isDarkTheme ? "dark-color" : ""}`}>
                        {row.exactTime}
                    </span>
                </div>
            </td>
        </tr>
    )
}

export default AdminStatsView
